import React from 'react';
import { Header } from '../components/Header';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import {
    Building2,
    Users,
    Briefcase,
    Globe,
    Shield,
    Zap,
    Clock,
    CheckCircle,
    TrendingUp,
    BarChart3,
    Headphones,
    FileText,
    Mail,
    Phone,
    MapPin,
    ArrowRight,
    Star,
    Target,
    Award,
    Sparkles,
    Rocket,
    HandshakeIcon,
    Scale,
    Heart,
    MessageSquare
} from 'lucide-react';

const EnterprisePage: React.FC = () => {
    const navigate = useNavigate();
    const { t } = useLanguage();

    const handleHome = () => {
        navigate('/');
    };

    const handleNewChat = () => {
        navigate('/');
    };

    // 企业服务特性
    const enterpriseFeatures = [
        {
            icon: <Users className="w-8 h-8" />,
            title: t('员工签证管理', 'Employee Visa Management'),
            description: t('为企业提供员工签证申请、续签、转换的一站式管理服务', 'One-stop support for employee visa applications, renewals, and switches.'),
            benefits: [t('批量申请处理', 'Batch applications'), t('进度实时追踪', 'Live progress tracking'), t('到期自动提醒', 'Expiry reminders')]
        },
        {
            icon: <Briefcase className="w-8 h-8" />,
            title: t('技术工签担保', 'Skilled Worker Sponsorship'),
            description: t('协助企业申请担保资质，管理工签配额和员工签证', 'Help employers obtain sponsor licences and manage visa allocation.'),
            benefits: [t('担保资质申请', 'Sponsor licence applications'), t('配额管理', 'Allocation management'), t('合规性检查', 'Compliance checks')]
        },
        {
            icon: <Globe className="w-8 h-8" />,
            title: t('全球人才引进', 'Global Talent Hiring'),
            description: t('帮助企业引进全球顶尖人才，处理全球人才签证申请', 'Help companies bring in top global talent and plan visa routes.'),
            benefits: [t('人才评估', 'Talent assessment'), t('签证规划', 'Visa planning'), t('快速通道', 'Fast track')]
        },
        {
            icon: <Shield className="w-8 h-8" />,
            title: t('合规风险管理', 'Compliance Risk Management'),
            description: t('确保企业签证操作符合UKVI要求，降低法律风险', 'Keep visa operations aligned with UKVI requirements and reduce legal risk.'),
            benefits: [t('合规审查', 'Compliance reviews'), t('风险预警', 'Risk alerts'), t('政策更新', 'Policy updates')]
        },
        {
            icon: <TrendingUp className="w-8 h-8" />,
            title: t('数据分析报告', 'Analytics Reports'),
            description: t('提供企业签证数据分析，优化人才引进策略', 'Analyse visa data to improve hiring and mobility strategy.'),
            benefits: [t('数据可视化', 'Data visualisation'), t('趋势分析', 'Trend analysis'), t('成本优化', 'Cost optimisation')]
        },
        {
            icon: <Headphones className="w-8 h-8" />,
            title: t('专属客户经理', 'Dedicated Account Manager'),
            description: t('为企业配备专属客户经理，提供一对一专业服务', 'A dedicated manager provides one-to-one professional support.'),
            benefits: [t('7x24小时支持', '24/7 support'), t('紧急处理', 'Urgent handling'), t('定期回访', 'Regular reviews')]
        }
    ];

    // 企业服务套餐
    const servicePlans = [
        {
            name: t('基础版', 'Basic'),
            price: '¥9,800',
            period: t('/年起', '/year from'),
            description: t('适合小型企业，基础签证需求', 'For small businesses with basic visa needs.'),
            features: [
                t('50个员工签证名额', '50 employee visa slots'),
                t('基础合规检查', 'Basic compliance checks'),
                t('在线签证管理平台', 'Online visa management platform'),
                t('邮件支持', 'Email support'),
                t('季度报告', 'Quarterly reports')
            ],
            limitations: [
                t('不含担保资质申请', 'Sponsor licence application not included'),
                t('不含紧急处理', 'Urgent handling not included'),
                t('响应时间24小时内', 'Response within 24 hours')
            ],
            recommended: false,
            color: 'from-gray-500 to-gray-600'
        },
        {
            name: t('专业版', 'Professional'),
            price: '¥29,800',
            period: t('/年起', '/year from'),
            description: t('适合中型企业，完整签证管理', 'For mid-sized companies needing complete visa management.'),
            features: [
                t('200个员工签证名额', '200 employee visa slots'),
                t('完整合规风险管理', 'Full compliance risk management'),
                t('专属客户经理', 'Dedicated account manager'),
                t('7x24小时电话支持', '24/7 phone support'),
                t('月度分析报告', 'Monthly analytics reports'),
                t('担保资质申请', 'Sponsor licence application'),
                t('紧急处理通道', 'Urgent handling channel')
            ],
            limitations: [],
            recommended: true,
            color: 'from-indigo-500 to-indigo-600'
        },
        {
            name: t('企业版', 'Enterprise'),
            price: '¥59,800',
            period: t('/年起', '/year from'),
            description: t('适合大型企业，定制化解决方案', 'For large organisations needing tailored solutions.'),
            features: [
                t('不限员工签证名额', 'Unlimited employee visa slots'),
                t('定制合规方案', 'Tailored compliance plan'),
                t('高级数据洞察', 'Advanced data insights'),
                t('战略咨询会议', 'Strategic consulting sessions'),
                t('全球人才引进', 'Global talent hiring'),
                t('法律支持服务', 'Legal support service'),
                t('API对接服务', 'API integration'),
                t('专属服务团队', 'Dedicated service team')
            ],
            limitations: [],
            recommended: false,
            color: 'from-purple-500 to-purple-600'
        }
    ];

    // 成功案例
    const successCases = [
        {
            company: t('某科技公司', 'A technology company'),
            industry: t('互联网', 'Internet'),
            employees: '50+',
            challenge: t('需要快速引进海外技术人才', 'Needed to quickly hire overseas technical talent.'),
            solution: t('协助申请担保资质，处理30+技术工签', 'Assisted with sponsor licence and 30+ Skilled Worker visas.'),
            result: t('3个月内完成团队搭建', 'Team built within 3 months.'),
            icon: '🚀'
        },
        {
            company: t('某金融集团', 'A financial group'),
            industry: t('金融', 'Finance'),
            employees: '200+',
            challenge: t('全球人才引进合规风险', 'Compliance risk in global talent hiring.'),
            solution: t('建立合规体系，统一签证管理', 'Built a compliance framework and centralised visa management.'),
            result: t('零违规记录，效率提升60%', 'Zero violations and 60% efficiency improvement.'),
            icon: '💼'
        },
        {
            company: t('某教育机构', 'An education provider'),
            industry: t('教育', 'Education'),
            employees: '80+',
            challenge: t('教师签证多样化需求', 'Diverse visa needs for teaching staff.'),
            solution: t('定制化签证方案，批量处理', 'Created tailored visa plans and batch processing.'),
            result: t('签证成功率98%', '98% visa success rate.'),
            icon: '🎓'
        }
    ];

    // 合作伙伴
    const partners = [
        { name: 'TechCorp', icon: '🏢' },
        { name: 'FinanceHub', icon: '💰' },
        { name: 'EduGlobal', icon: '📚' },
        { name: 'InnovateLab', icon: '🔬' },
        { name: 'StartUpX', icon: '⚡' },
        { name: 'LegalPlus', icon: '⚖️' }
    ];

    // 常见问题
    const faqs = [
        {
            question: t('企业如何申请担保资质？', 'How can a company apply for a sponsor licence?'),
            answer: t('我们提供全程指导服务，包括材料准备、申请提交、面试辅导等，确保企业顺利获得担保资质。', 'We provide end-to-end guidance, including document preparation, submission, and interview coaching.')
        },
        {
            question: t('签证管理平台如何使用？', 'How does the visa management platform work?'),
            answer: t('提供在线管理系统，可实时查看员工签证状态、到期提醒、合规检查等功能，简单易用。', 'The online platform lets you view visa status, expiry reminders, and compliance checks in real time.')
        },
        {
            question: t('可以定制化服务方案吗？', 'Can services be customised?'),
            answer: t('是的，我们为企业提供定制化解决方案，根据企业规模和需求，设计最适合的服务方案。', 'Yes. We design tailored plans based on company size and needs.')
        },
        {
            question: t('紧急签证需求如何处理？', 'How are urgent visa needs handled?'),
            answer: t('专业版和企业版用户享有紧急处理通道，可在24小时内完成加急申请。', 'Professional and Enterprise users get an urgent handling channel for rapid applications.')
        }
    ];

    return (
        <div className="min-h-screen bg-white dark:bg-[#0f172a]">
            <Header onClearChat={handleNewChat} onHome={handleHome} showNewChat={true} />

            {/* Hero 区域 */}
            <div className="relative overflow-hidden bg-gradient-to-br from-indigo-600 via-indigo-700 to-indigo-800 dark:from-indigo-900 dark:via-indigo-800 dark:to-indigo-900 text-white">
                <div className="absolute inset-0 bg-black opacity-10"></div>
                <div className="absolute inset-0 bg-gradient-to-br from-purple-600/20 to-pink-600/20"></div>

                <div className="relative max-w-6xl mx-auto px-4 py-20 sm:px-6 lg:px-8">
                    <div className="text-center max-w-3xl mx-auto">
                        <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                            <Building2 className="w-4 h-4" />
                            <span className="text-sm font-medium">{t('企业服务', 'Enterprise Services')}</span>
                        </div>
                        <h1 className="text-4xl sm:text-5xl font-bold mb-6">
                            {t('企业签证解决方案', 'Enterprise Visa Solutions')}
                        </h1>
                        <p className="text-xl mb-8 text-indigo-100">
                            {t('为全球企业提供专业、高效、合规的员工签证管理服务', 'Professional, efficient, and compliant employee visa management for global businesses.')}
                        </p>

                        {/* 快速咨询区域 */}
                        <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-6 sm:p-8">
                            <div className="flex flex-col items-center gap-4">
                                <div className="flex items-center gap-2 text-xl font-semibold">
                                    <Sparkles className="w-5 h-5" />
                                    <span>{t('快速咨询企业服务', 'Ask about enterprise services')}</span>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full max-w-2xl">
                                    {[
                                        t('如何申请担保资质？', 'How do we apply for a sponsor licence?'),
                                        t('员工签证费用多少？', 'How much do employee visas cost?'),
                                        t('合规风险管理', 'Compliance risk management'),
                                        t('紧急签证处理', 'Urgent visa handling')
                                    ].map((question, index) => (
                                        <button
                                            key={index}
                                            onClick={() => navigate('/')}
                                            className="flex items-center justify-between px-4 py-3 bg-white/20 hover:bg-white/30 rounded-lg transition-colors text-left group"
                                        >
                                            <span className="text-sm sm:text-base">{question}</span>
                                            <ArrowRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                                        </button>
                                    ))}
                                </div>

                                <button
                                    onClick={() => navigate('/')}
                                    className="mt-4 px-8 py-3 bg-white text-indigo-600 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg hover:shadow-xl inline-flex items-center gap-2"
                                >
                                    <MessageSquare className="w-5 h-5" />
                                    {t('咨询客服', 'Contact support')}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* 核心优势 */}
            <div className="bg-white dark:bg-[#0f172a] border-b border-gray-200 dark:border-gray-800">
                <div className="max-w-6xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-4">{t('核心优势', 'Core Advantages')}</h2>
                    <p className="text-xl text-center text-gray-600 dark:text-gray-400 mb-12 max-w-3xl mx-auto">
                        {t('为企业提供全方位的签证管理解决方案', 'End-to-end visa management solutions for employers.')}
                    </p>

                    <div className="grid md:grid-cols-3 gap-8">
                        <div className="text-center">
                            <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Scale className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('专业合规', 'Professional Compliance')}</h3>
                            <p className="text-gray-600 dark:text-gray-400">{t('专业移民律师团队，确保100%合规', 'Immigration law expertise to support compliant operations.')}</p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Zap className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('高效处理', 'Efficient Processing')}</h3>
                            <p className="text-gray-600 dark:text-gray-400">{t('智能系统+专业团队，处理效率提升80%', 'Smart systems plus expert support improve processing efficiency by 80%.')}</p>
                        </div>
                        <div className="text-center">
                            <div className="w-16 h-16 bg-indigo-100 dark:bg-indigo-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                                <HandshakeIcon className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{t('一站式服务', 'One-stop Service')}</h3>
                            <p className="text-gray-600 dark:text-gray-400">{t('从担保资质到员工签证，全程无忧', 'From sponsor licence to employee visas, everything is handled in one place.')}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* 企业服务特性 */}
            <div className="max-w-6xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
                <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-4">{t('企业服务特性', 'Enterprise Service Features')}</h2>
                <p className="text-xl text-center text-gray-600 dark:text-gray-400 mb-12 max-w-3xl mx-auto">
                    {t('为企业量身打造的专业签证服务', 'Professional visa services tailored for employers.')}
                </p>

                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {enterpriseFeatures.map((feature, index) => (
                        <div
                            key={index}
                            className="bg-white dark:bg-[#1e293b] rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-all group"
                        >
                            <div className="w-14 h-14 bg-indigo-100 dark:bg-indigo-900/30 rounded-lg flex items-center justify-center mb-4 text-indigo-600 dark:text-indigo-400 group-hover:scale-110 transition-transform">
                                {feature.icon}
                            </div>
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{feature.title}</h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{feature.description}</p>
                            <ul className="space-y-2">
                                {feature.benefits.map((benefit, i) => (
                                    <li key={i} className="text-xs text-gray-500 dark:text-gray-500 flex items-center gap-2">
                                        <CheckCircle className="w-3 h-3 text-indigo-600 dark:text-indigo-400" />
                                        {benefit}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </div>

            {/* 服务套餐 */}
            <div className="bg-gray-50 dark:bg-[#1e293b] border-y border-gray-200 dark:border-gray-800">
                <div className="max-w-6xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-4">{t('服务套餐', 'Service Plans')}</h2>
                    <p className="text-xl text-center text-gray-600 dark:text-gray-400 mb-12 max-w-3xl mx-auto">
                        {t('选择适合企业的服务方案', 'Choose the service plan that fits your business.')}
                    </p>

                    <div className="grid md:grid-cols-3 gap-6">
                        {servicePlans.map((plan, index) => (
                            <div
                                key={index}
                                className={`relative bg-white dark:bg-[#0f172a] rounded-xl shadow-sm border ${plan.recommended
                                        ? 'border-indigo-500 dark:border-indigo-600 shadow-lg scale-105'
                                        : 'border-gray-200 dark:border-gray-700'
                                    }`}
                            >
                                {plan.recommended && (
                                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                                        <span className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white text-xs px-3 py-1 rounded-full">
                                            {t('推荐', 'Recommended')}
                                        </span>
                                    </div>
                                )}

                                <div className="p-6">
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{plan.name}</h3>
                                    <div className="flex items-baseline mb-4">
                                        <span className="text-3xl font-bold text-gray-900 dark:text-white">{plan.price}</span>
                                        <span className="text-gray-500 dark:text-gray-400 ml-1">{plan.period}</span>
                                    </div>
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">{plan.description}</p>

                                    <ul className="space-y-3 mb-6">
                                        {plan.features.map((feature, i) => (
                                            <li key={i} className="text-sm text-gray-700 dark:text-gray-300 flex items-start gap-2">
                                                <CheckCircle className="w-4 h-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0 mt-0.5" />
                                                <span>{feature}</span>
                                            </li>
                                        ))}
                                        {plan.limitations.map((limitation, i) => (
                                            <li key={i} className="text-sm text-gray-400 dark:text-gray-500 flex items-start gap-2">
                                                <span className="w-4 h-4 flex-shrink-0">•</span>
                                                <span>{limitation}</span>
                                            </li>
                                        ))}
                                    </ul>

                                    <button
                                        onClick={() => navigate('/')}
                                        className={`w-full py-2 px-4 rounded-lg font-semibold transition-colors ${plan.recommended
                                                ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                                                : 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-200 dark:hover:bg-gray-700'
                                            }`}
                                    >
                                        {t('咨询方案', 'Ask about this plan')}
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* 成功案例 */}
            <div className="max-w-6xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
                <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-4">{t('成功案例', 'Success Stories')}</h2>
                <p className="text-xl text-center text-gray-600 dark:text-gray-400 mb-12 max-w-3xl mx-auto">
                    {t('帮助企业解决签证难题，实现人才引进', 'Helping companies solve visa challenges and hire global talent.')}
                </p>

                <div className="grid md:grid-cols-3 gap-6">
                    {successCases.map((case_, index) => (
                        <div key={index} className="bg-white dark:bg-[#1e293b] rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                            <div className="flex items-center gap-3 mb-4">
                                <span className="text-3xl">{case_.icon}</span>
                                <div>
                                    <h3 className="font-semibold text-gray-900 dark:text-white">{case_.company}</h3>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{case_.industry}</p>
                                </div>
                            </div>
                            <div className="space-y-3">
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('挑战', 'Challenge')}</p>
                                    <p className="text-sm text-gray-700 dark:text-gray-300">{case_.challenge}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('解决方案', 'Solution')}</p>
                                    <p className="text-sm text-gray-700 dark:text-gray-300">{case_.solution}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">{t('成果', 'Result')}</p>
                                    <p className="text-sm font-medium text-indigo-600 dark:text-indigo-400">{case_.result}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* 合作伙伴 */}
            <div className="bg-gray-50 dark:bg-[#1e293b] border-y border-gray-200 dark:border-gray-800">
                <div className="max-w-6xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
                    <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-4">{t('合作伙伴', 'Partners')}</h2>
                    <p className="text-xl text-center text-gray-600 dark:text-gray-400 mb-12 max-w-3xl mx-auto">
                        {t('信赖我们的企业', 'Companies that trust us.')}
                    </p>

                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
                        {partners.map((partner, index) => (
                            <div key={index} className="text-center">
                                <div className="w-16 h-16 bg-white dark:bg-[#0f172a] rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 flex items-center justify-center mx-auto mb-2 text-2xl">
                                    {partner.icon}
                                </div>
                                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">{partner.name}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* 常见问题 */}
            <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
                <h2 className="text-3xl font-bold text-center text-gray-900 dark:text-white mb-12">{t('常见问题', 'FAQ')}</h2>

                <div className="space-y-4">
                    {faqs.map((faq, index) => (
                        <div key={index} className="bg-white dark:bg-[#1e293b] rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                            <h3 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
                                <span className="text-indigo-600 dark:text-indigo-400">Q{index + 1}.</span>
                                {faq.question}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 ml-6">{faq.answer}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* 行动号召 */}
            <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 text-white">
                <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8 text-center">
                    <h2 className="text-3xl font-bold mb-4">{t('为企业提供专业签证服务', 'Professional Visa Services for Employers')}</h2>
                    <p className="text-xl mb-8 opacity-90 max-w-2xl mx-auto">
                        {t('让专业团队帮您管理员工签证，专注核心业务发展', 'Let a professional team manage employee visas while you focus on the business.')}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button
                            onClick={() => navigate('/')}
                            className="bg-white text-indigo-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors shadow-lg hover:shadow-xl inline-flex items-center gap-2"
                        >
                            <MessageSquare className="w-5 h-5" />
                            {t('咨询客服', 'Contact support')}
                        </button>
                        <button
                            onClick={() => navigate('/')}
                            className="bg-indigo-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-indigo-600 transition-colors shadow-lg hover:shadow-xl inline-flex items-center gap-2 border border-indigo-400"
                        >
                            <FileText className="w-5 h-5" />
                            {t('下载服务手册', 'Download brochure')}
                        </button>
                    </div>
                </div>
            </div>

            {/* 底部版权 */}
            <footer className="bg-gray-900 text-white">
                <div className="max-w-6xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
                    <div className="text-center text-sm text-gray-400">
                        <p>{t('© 2026 英国签证AI助手. 保留所有权利。', '© 2026 UK Visa AI Assistant. All rights reserved.')}</p>
                        <p className="mt-2">
                            <a href="#" className="hover:text-white transition-colors">{t('ICP证: 京ICP备12345678号', 'ICP: Beijing ICP 12345678')}</a> |
                            <a href="#" className="hover:text-white transition-colors ml-2">{t('营业执照', 'Business License')}</a> |
                            <a href="#" className="hover:text-white transition-colors ml-2">{t('隐私政策', 'Privacy Policy')}</a>
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default EnterprisePage;
